import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Globe, Loader2 } from 'lucide-react';

export default function Login() {
  const { t, toggleLanguage, language, isRTL } = useLanguage();
  const { login, error } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const success = await login(email, password);
    
    setIsLoading(false);
    if (success) {
      navigate('/admin');
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{
        background: 'linear-gradient(135deg, #0a1520 0%, #0f3d3e 50%, #1a2a3a 100%)',
      }}
    >
      {/* Language Toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={toggleLanguage}
        className="absolute top-4 end-4 bg-white/90 backdrop-blur-sm"
        data-testid="login-language-toggle"
      >
        <Globe className="w-4 h-4 me-2" />
        {language === 'ar' ? 'English' : 'العربية'}
      </Button>

      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-xl">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-[#0A4C3A] rounded-xl flex items-center justify-center mb-2">
            <span className="text-white font-bold text-3xl">ن</span>
          </div>
          <CardTitle className="text-2xl font-bold text-[#0A4C3A]">
            {t('app_name')}
          </CardTitle>
          <CardDescription className="text-gray-600">
            {t('login_subtitle')}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div 
                className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">
                {t('username')}
              </Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('username')}
                required
                className="border-gray-300 focus:border-[#0A4C3A] focus:ring-[#0A4C3A]"
                data-testid="login-username"
                dir="ltr"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">
                {t('password')}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('password')}
                required
                className="border-gray-300 focus:border-[#0A4C3A] focus:ring-[#0A4C3A]"
                data-testid="login-password"
                dir="ltr"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[#0A4C3A] hover:bg-[#073B2D] text-white"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                t('login')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
